/**
 * The per-user permission-override editor — the legacy "named-person rules",
 * as data. AUTHORED.
 *
 * ===========================================================================
 * THE PICKER IS THE SHARED REGISTRY, GROUPED — AND THE GRANT STOPS ARE D5'S
 * ===========================================================================
 * Options come from `permissionGroups()` (`domain/rbac.ts` — the tested mirror
 * of `PERMISSION_KEYS` in `@crf/shared`), rendered as `<optgroup>`s so the
 * 43-key list reads BY CONCERN. Nothing is hand-listed: a key added to the
 * shared registry appears here by construction or fails the mirror test.
 *
 * ⚠ UPDATED: THE OPTION IS NO LONGER THE RAW KEY. It used to read `{key}` verbatim,
 * which asked the operator to grant `crf.view` without being told that it also opens six
 * delivery registers and a colleague's user card. Every option now reads
 * `<human label> (<key>)`, carries the description in its `title`, and is suffixed `⚠`
 * when the key grants more than its name says — with the warning itself rendered
 * VISIBLY under the picker for the current selection, because a fact that only exists on
 * hover is a fact nobody reads. The words are DATA in `domain/permission-catalog.ts`;
 * `permission-labels.test.tsx` proves they reach the screen.
 *
 * Two grant rules from `assertOverridesInScope` (d5-boundary.ts:99) are
 * rendered, not merely relied on:
 *
 *  1. `config.global`, `dashboard.group`, `remote.access` are OVERRIDE-ONLY
 *     (no role template grants them) and only a group administrator may GRANT
 *     one. Every caller sees them labelled "override-only"; for a non-grp
 *     caller they are disabled options under "Grant" — visible (this editor is
 *     the one place those keys exist at all, hiding them would erase the
 *     concept) but not selectable. Under "Revoke" they enable again: revoking
 *     stays open to anyone inside the country boundary.
 *  2. Nobody may grant a permission they do not themselves hold in that
 *     country — the escalation stop. The option disables with the reason in
 *     its title, computed from the caller's own token (`session.permsIn`),
 *     which is exactly the data the server judges with.
 *
 * The server re-checks both; these gates buy the honest form, not the control.
 */

import { useId, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '../../components/ui';
import { useSession } from '../../auth/session';
import { isOverrideOnly, permissionGroups } from '../../domain/rbac';
import { OUT_POWERS_PREFIX, permissionMeta } from '../../domain/permission-catalog';
import type { CountryCode } from '../../domain/countries';
import type { PermissionKey } from '../../lib/permissions';
import type { PermissionOverride } from './users-api';

const SELECT_CLASSES =
  'h-9 max-w-full rounded-sm border border-line bg-subtle px-2.5 text-body text-fg outline-none';

/**
 * An option's hover text: what the key means, then why it is refused (if it is).
 *
 * ⚠ THE TOOLTIP IS NOT THE WARNING'S ONLY HOME. A native `<option>` cannot hold two
 * lines, and a fact that only exists on hover is a fact a hurried administrator never
 * reads — so the out-powering sentence is ALSO rendered, visibly, under the picker for
 * whatever is currently selected (see `PermissionNote` below). This string is the
 * convenience; that paragraph is the promise, and `permission-labels.test.tsx` holds it.
 *
 * The blocked REASON goes last and is never dropped: it is the one part of this text
 * that explains a control the operator can see is disabled.
 */
function optionTitle(key: PermissionKey, reason: string | undefined): string {
  const meta = permissionMeta(key);
  const parts = [meta.description];
  if (meta.outPowersName !== null) parts.push(OUT_POWERS_PREFIX + meta.outPowersName);
  if (reason !== undefined) parts.push(reason);
  return parts.join(' ');
}

/**
 * The description — and, where there is one, the warning — for the permission the
 * operator has selected RIGHT NOW, rendered where the decision is made.
 *
 * `role="note"` rather than `role="alert"`: this is a standing property of the key, on
 * screen from first paint, not an event that should interrupt a screen reader.
 */
function PermissionNote({ permission }: { permission: PermissionKey }): React.JSX.Element {
  const meta = permissionMeta(permission);
  return (
    <div className="flex w-full flex-col gap-0.5">
      <p className="text-label text-fg-secondary">{meta.description}</p>
      {meta.outPowersName === null ? null : (
        <p role="note" className="text-label text-warn-fg">
          {OUT_POWERS_PREFIX}
          {meta.outPowersName}
        </p>
      )}
    </div>
  );
}

export interface OverridesEditorProps {
  value: readonly PermissionOverride[];
  onChange: (next: PermissionOverride[]) => void;
  /** The caller's manageable countries — the only addable ones (D5). */
  countries: readonly CountryCode[];
  /** Out-of-scope overrides, shown locked. */
  lockedOverrides?: readonly PermissionOverride[];
  disabled?: boolean;
}

export function OverridesEditor({
  value,
  onChange,
  countries,
  lockedOverrides = [],
  disabled = false,
}: OverridesEditorProps): React.JSX.Element {
  const { isGroupAdmin, permsIn } = useSession();
  const countryId = useId();
  const permissionId = useId();
  const effectId = useId();

  const [country, setCountry] = useState<CountryCode | ''>(countries[0] ?? '');
  const [permission, setPermission] = useState<PermissionKey>('inquiries.view');
  const [effect, setEffect] = useState<'grant' | 'revoke'>('grant');

  const keyOf = (entry: PermissionOverride): string =>
    `${entry.country}:${entry.permission}:${entry.effect}`;

  const exists = (candidate: PermissionOverride): boolean =>
    value.some((entry) => keyOf(entry) === keyOf(candidate));

  /**
   * May the CALLER grant `key` in `country`? Mirrors d5-boundary.ts:118-134.
   * Only consulted when effect is 'grant' — revokes have no escalation risk.
   */
  const grantable = (key: PermissionKey, target: CountryCode | ''): boolean => {
    if (isGroupAdmin) return true;
    if (target === '') return false;
    if (isOverrideOnly(key)) return false;
    return permsIn(target).includes(key);
  };

  const grantBlockedReason = (key: PermissionKey): string | undefined => {
    if (effect !== 'grant' || isGroupAdmin) return undefined;
    if (isOverrideOnly(key)) return 'Only a group administrator may grant this.';
    if (country !== '' && !permsIn(country).includes(key)) {
      return `You cannot grant a permission you do not hold in ${country}.`;
    }
    return undefined;
  };

  const candidate: PermissionOverride | null =
    country === '' ? null : { country, permission, effect };
  const blocked =
    candidate !== null && candidate.effect === 'grant' && !grantable(permission, country);
  const duplicate = candidate !== null && exists(candidate);

  const add = (): void => {
    if (candidate === null || blocked || duplicate) return;
    onChange([...value, candidate]);
  };

  const remove = (entry: PermissionOverride): void => {
    onChange(value.filter((existing) => keyOf(existing) !== keyOf(entry)));
  };

  return (
    <div className="flex flex-col gap-3">
      {value.length === 0 && lockedOverrides.length === 0 ? (
        <p className="text-label text-fg-secondary">
          No overrides. This user’s permissions come entirely from their roles.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {value.map((entry) => (
            <li
              key={keyOf(entry)}
              className="flex h-9 items-center justify-between gap-2 rounded-sm border border-line bg-surface px-2.5"
            >
              <span className="flex min-w-0 items-center gap-2 text-body13 text-fg">
                <span className="font-mono text-mono text-fg-secondary">{entry.country}</span>
                <span className="truncate">{permissionMeta(entry.permission).label}</span>
                <span className="truncate font-mono text-mono text-fg-secondary">
                  {entry.permission}
                </span>
                <EffectWord effect={entry.effect} />
              </span>
              {disabled ? null : (
                <button
                  type="button"
                  onClick={() => remove(entry)}
                  aria-label={`Remove the ${entry.effect} of ${entry.permission} in ${entry.country}`}
                  className="shrink-0 text-fg-muted hover:text-fg"
                >
                  <X aria-hidden="true" className="size-3.5" />
                </button>
              )}
            </li>
          ))}
          {lockedOverrides.map((entry) => (
            <li
              key={`locked-${keyOf(entry)}`}
              className="flex h-9 items-center justify-between gap-2 rounded-sm border border-line bg-subtle px-2.5"
              title={`You do not hold users.manage in ${entry.country}, so this override is read-only for you.`}
            >
              <span className="flex min-w-0 items-center gap-2 text-body13 text-fg-secondary">
                <span className="font-mono text-mono">{entry.country}</span>
                <span className="truncate font-mono text-mono">{entry.permission}</span>
                <EffectWord effect={entry.effect} />
              </span>
              <span className="shrink-0 text-label text-fg-secondary">Outside your scope</span>
            </li>
          ))}
        </ul>
      )}

      {disabled ? null : (
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor={countryId} className="text-label font-medium text-fg">
              Country
            </label>
            <select
              id={countryId}
              value={country}
              onChange={(event) => {
                const next = event.target.value;
                setCountry(next === '' ? '' : (next as CountryCode));
              }}
              className={SELECT_CLASSES}
            >
              {countries.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor={effectId} className="text-label font-medium text-fg">
              Effect
            </label>
            <select
              id={effectId}
              value={effect}
              onChange={(event) => setEffect(event.target.value as 'grant' | 'revoke')}
              className={SELECT_CLASSES}
            >
              <option value="grant">Grant</option>
              <option value="revoke">Revoke</option>
            </select>
          </div>
          <div className="flex min-w-0 flex-col gap-1.5">
            <label htmlFor={permissionId} className="text-label font-medium text-fg">
              Permission
            </label>
            <select
              id={permissionId}
              value={permission}
              onChange={(event) => setPermission(event.target.value as PermissionKey)}
              className={SELECT_CLASSES}
            >
              {permissionGroups().map((group) => (
                <optgroup key={group.title} label={group.title}>
                  {group.keys.map((key) => {
                    const reason = grantBlockedReason(key);
                    const meta = permissionMeta(key);
                    return (
                      <option
                        key={key}
                        value={key}
                        disabled={reason !== undefined}
                        title={optionTitle(key, reason)}
                      >
                        {meta.label} ({key})
                        {isOverrideOnly(key) ? ' — override-only' : ''}
                        {meta.outPowersName === null ? '' : ' ⚠'}
                      </option>
                    );
                  })}
                </optgroup>
              ))}
            </select>
          </div>
          <Button
            variant="ghost"
            onClick={add}
            disabled={candidate === null || blocked || duplicate}
            title={
              blocked
                ? grantBlockedReason(permission)
                : duplicate
                  ? 'This override already exists.'
                  : undefined
            }
          >
            <Plus aria-hidden="true" className="size-4" />
            Add override
          </Button>
          <PermissionNote permission={permission} />
        </div>
      )}
    </div>
  );
}

/**
 * The effect, as a word with the status pair that means it: grant = the
 * success pair, revoke = the danger pair. Both are the AA-nudged pill pairs
 * (4.55:1 / 4.52:1) — measured in tokens.css, re-asserted in the admin
 * contrast suite.
 */
function EffectWord({ effect }: { effect: 'grant' | 'revoke' }): React.JSX.Element {
  return effect === 'grant' ? (
    <span className="shrink-0 rounded-pill bg-ok-bg px-2 py-0.5 text-label font-medium text-ok-fg">
      Grant
    </span>
  ) : (
    <span className="shrink-0 rounded-pill bg-danger-bg px-2 py-0.5 text-label font-medium text-danger-fg">
      Revoke
    </span>
  );
}
